import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertTriangle, Send } from "lucide-react";
import type { Lead, DispositionReason, FollowUpType, NoDemoDisposition } from "@/data/pipelineTypes";

const DISPOSITION_REASONS: { value: DispositionReason; label: string }[] = [
  { value: "no_show", label: "No Show — Homeowner not present" },
  { value: "not_home", label: "Not Home — Arrived, no one answered" },
  { value: "cancelled", label: "Cancelled — Customer cancelled before/at arrival" },
  { value: "refused_entry", label: "Refused Entry — Customer declined consultation" },
  { value: "safety_concern", label: "Safety Concern — Unsafe site conditions" },
  { value: "wrong_address", label: "Wrong Address — Incorrect location info" },
  { value: "not_qualified", label: "Not Qualified — Does not meet criteria" },
  { value: "other", label: "Other" },
];

const CONTACT_METHODS = ["Phone Call", "Text Message", "Email", "In-Person Visit"];

interface Props {
  lead: Lead;
  open: boolean;
  onClose: () => void;
  onSubmit: (disposition: NoDemoDisposition) => void;
}

export function NoDemoDispositionForm({ lead, open, onClose, onSubmit }: Props) {
  const [reason, setReason] = useState<DispositionReason | "">("");
  const [homeownerPresent, setHomeownerPresent] = useState<string>("");
  const [decisionMakerPresent, setDecisionMakerPresent] = useState<string>("");
  const [mainReasonText, setMainReasonText] = useState("");
  const [pricingDiscussed, setPricingDiscussed] = useState<string>("");
  const [stillLive, setStillLive] = useState<string>("");
  const [followUpType, setFollowUpType] = useState<FollowUpType | "">("");
  const [followUpNotes, setFollowUpNotes] = useState("");
  const [bestContact, setBestContact] = useState("");
  const [followUpDateTime, setFollowUpDateTime] = useState("");
  const [noFollowUpReason, setNoFollowUpReason] = useState("");

  const needsFollowUpDate = followUpType === "rep" || followUpType === "manager";
  const needsNoFollowUpReason = followUpType === "none";

  const isValid =
    reason !== "" &&
    homeownerPresent !== "" &&
    decisionMakerPresent !== "" &&
    mainReasonText.trim() !== "" &&
    pricingDiscussed !== "" &&
    stillLive !== "" &&
    followUpType !== "" &&
    bestContact !== "" &&
    (!needsFollowUpDate || followUpDateTime !== "") &&
    (!needsNoFollowUpReason || noFollowUpReason.trim() !== "");

  const handleSubmit = () => {
    if (!isValid) return;
    const disposition: NoDemoDisposition = {
      reason: reason as DispositionReason,
      homeownerPresent: homeownerPresent === "yes",
      decisionMakerPresent: decisionMakerPresent === "yes",
      mainReasonNoDemoText: mainReasonText.trim(),
      pricingDiscussed: pricingDiscussed === "yes",
      stillLiveOpportunity: stillLive === "yes",
      followUpType: followUpType as FollowUpType,
      followUpNotes: followUpNotes.trim(),
      bestContactMethod: bestContact,
      followUpDateTime: needsFollowUpDate ? followUpDateTime : undefined,
      noFollowUpReason: needsNoFollowUpReason ? noFollowUpReason.trim() : undefined,
      completedAt: new Date().toISOString(),
      completedBy: lead.assignedRep,
    };
    onSubmit(disposition);
  };

  const YesNoField = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
    <div className="space-y-2">
      <Label className="text-[12px] font-semibold">{label}</Label>
      <RadioGroup value={value} onValueChange={onChange} className="flex gap-4">
        <div className="flex items-center gap-2">
          <RadioGroupItem value="yes" id={`${label}-yes`} />
          <Label htmlFor={`${label}-yes`} className="text-[12px]">Yes</Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="no" id={`${label}-no`} />
          <Label htmlFor={`${label}-no`} className="text-[12px]">No</Label>
        </div>
      </RadioGroup>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) return; }}>
      <DialogContent className="max-w-[600px] max-h-[90vh] p-0 gap-0 [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader className="px-5 py-4 border-b border-border bg-destructive/[0.03]">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </div>
            <div>
              <DialogTitle className="text-[15px] font-bold">No-Demo Disposition — {lead.primaryName}</DialogTitle>
              <DialogDescription className="text-[11px] text-muted-foreground mt-0.5">
                Complete all fields before closing this appointment.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[65vh]">
          <div className="p-5 space-y-5">
            {/* Disposition Reason */}
            <div className="space-y-2">
              <Label className="text-[12px] font-semibold">Disposition Reason *</Label>
              <Select value={reason} onValueChange={(v) => setReason(v as DispositionReason)}>
                <SelectTrigger className="text-[12px]"><SelectValue placeholder="Select reason..." /></SelectTrigger>
                <SelectContent>
                  {DISPOSITION_REASONS.map(r => (
                    <SelectItem key={r.value} value={r.value} className="text-[12px]">{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Yes/No Questions */}
            <div className="grid grid-cols-2 gap-4">
              <YesNoField label="Was homeowner present?" value={homeownerPresent} onChange={setHomeownerPresent} />
              <YesNoField label="Was decision maker present?" value={decisionMakerPresent} onChange={setDecisionMakerPresent} />
            </div>

            {/* Main reason */}
            <div className="space-y-2">
              <Label className="text-[12px] font-semibold">Main reason demo did not occur *</Label>
              <Textarea value={mainReasonText} onChange={(e) => setMainReasonText(e.target.value)}
                placeholder="Describe why the demo/consultation did not take place..."
                className="text-[12px] min-h-[70px]" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <YesNoField label="Was pricing discussed?" value={pricingDiscussed} onChange={setPricingDiscussed} />
              <YesNoField label="Is this still a live opportunity?" value={stillLive} onChange={setStillLive} />
            </div>

            {/* Follow-up Type */}
            <div className="space-y-2">
              <Label className="text-[12px] font-semibold">Follow-up type *</Label>
              <RadioGroup value={followUpType} onValueChange={(v) => setFollowUpType(v as FollowUpType)} className="space-y-2">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="rep" id="fu-rep" />
                  <Label htmlFor="fu-rep" className="text-[12px]">Rep Follow-Up — I will follow up</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="manager" id="fu-mgr" />
                  <Label htmlFor="fu-mgr" className="text-[12px]">Manager Follow-Up — Escalate for rehash/reprice</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="none" id="fu-none" />
                  <Label htmlFor="fu-none" className="text-[12px]">No Follow-Up Needed</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Conditional: Follow-up date/time */}
            {needsFollowUpDate && (
              <div className="space-y-2 p-3 rounded-lg border border-primary/20 bg-primary/[0.02]">
                <Label className="text-[12px] font-semibold">
                  Follow-up date & time *
                  {followUpType === "manager" && (
                    <span className="text-[10px] text-muted-foreground ml-2">(Manager task due in 2 business days)</span>
                  )}
                </Label>
                <Input type="datetime-local" value={followUpDateTime} onChange={(e) => setFollowUpDateTime(e.target.value)}
                  className="text-[12px]" />
              </div>
            )}

            {/* Conditional: No follow-up reason */}
            {needsNoFollowUpReason && (
              <div className="space-y-2 p-3 rounded-lg border border-warning/20 bg-warning/[0.02]">
                <Label className="text-[12px] font-semibold">Why is no follow-up needed? *</Label>
                <Textarea value={noFollowUpReason} onChange={(e) => setNoFollowUpReason(e.target.value)}
                  placeholder="Explain why this opportunity should not be followed up on..."
                  className="text-[12px] min-h-[60px]" />
              </div>
            )}

            {/* Follow-up notes */}
            <div className="space-y-2">
              <Label className="text-[12px] font-semibold">Follow-up notes</Label>
              <Textarea value={followUpNotes} onChange={(e) => setFollowUpNotes(e.target.value)}
                placeholder="Any additional context for the follow-up..."
                className="text-[12px] min-h-[60px]" />
            </div>

            {/* Best contact method */}
            <div className="space-y-2">
              <Label className="text-[12px] font-semibold">Best contact method *</Label>
              <Select value={bestContact} onValueChange={setBestContact}>
                <SelectTrigger className="text-[12px]"><SelectValue placeholder="Select method..." /></SelectTrigger>
                <SelectContent>
                  {CONTACT_METHODS.map(m => (
                    <SelectItem key={m} value={m} className="text-[12px]">{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </ScrollArea>

        <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-muted/20">
          <Button variant="ghost" size="sm" className="text-[12px]" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="text-[12px] font-bold gap-1.5" disabled={!isValid} onClick={handleSubmit}>
            <Send className="h-3.5 w-3.5" /> Submit Disposition
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
